import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ChevronDown, Check, X } from 'lucide-react';

/**
 * Lightweight multi-select with search.
 * Props:
 *   options: [{ value, label }]
 *   selected: Array<string>   (current selection)
 *   onChange: (next: string[]) => void
 *   placeholder: string
 *   searchable: boolean
 *   testId: string
 *   emptyLabel: string  (fallback if 0 selected — defaults to "All")
 */
export default function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Select…',
  searchable = false,
  testId,
  emptyLabel = 'All',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const allSelected = selected.length === options.length;
  const noneSelected = selected.length === 0;

  const toggle = (value) => {
    if (selected.includes(value)) onChange(selected.filter(v => v !== value));
    else onChange([...selected, value]);
  };

  const summary = noneSelected
    ? emptyLabel
    : allSelected
      ? `All (${options.length})`
      : selected.length <= 2
        ? options.filter(o => selected.includes(o.value)).map(o => o.label).join(', ')
        : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className={`truncate ${noneSelected ? 'text-muted-foreground' : ''}`}>{summary || placeholder}</span>
          <ChevronDown size={14} className="ml-2 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        {searchable && (
          <div className="p-2 border-b border-border">
            <Input
              autoFocus
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
        )}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <button
            type="button"
            onClick={() => onChange(options.map(o => o.value))}
            className="text-xs text-primary hover:underline"
            data-testid={testId ? `${testId}-select-all` : undefined}
          >
            <Check size={12} className="inline mr-1" /> Select all
          </button>
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-muted-foreground hover:underline"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X size={12} className="inline mr-1" /> Clear
          </button>
        </div>
        <div className="max-h-[260px] overflow-y-auto py-1">
          {filtered.length === 0 && (
            <p className="px-3 py-4 text-xs text-center text-muted-foreground">No results</p>
          )}
          {filtered.map(o => {
            const isOn = selected.includes(o.value);
            return (
              <label
                key={o.value}
                className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-muted/50 text-sm"
              >
                <Checkbox
                  checked={isOn}
                  onCheckedChange={() => toggle(o.value)}
                  data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                />
                <span className="flex-1 text-wv-body">{o.label}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
